/* eslint-disable @typescript-eslint/member-ordering */
import { Injectable } from '@angular/core';
import { SparePart } from '@ecs-features/result/spare-parts-result/spare-parts.interface';
import { SelectableRow } from '@os-components';
import { BehaviorSubject, Subject } from 'rxjs';
import { RecommendedPart } from 'src/app/components/parts-recommendation/parts-recommendation.interface';
import { DetailType } from './part-detail-types.enum';

@Injectable({
    providedIn: 'root',
})
export class PartDetailService {
    public isMarkedRowWithDetails = false;
    /**
     * True if the checked rows are containing at least one sidepanel information.
     */
    public checkedRowsIncludeDetails = false;

    public type?: DetailType;
    /**
     * Subject, that informs about checked recommendations.
     */
    private recommendationChecked$ = new Subject<{
        group: string;
        rows: SelectableRow<RecommendedPart>[];
    } | null>();

    public set checkRowsByRecommendation(value: { group: string; rows: SelectableRow<RecommendedPart>[] }) {
        this.recommendationChecked$.next(value);
    }

    public readonly getCheckRowsByRecommendation = this.recommendationChecked$.asObservable();

    /**
     * BehaviorSubject, that stores the currently checked rows of the main table.
     */
    private checkedRows$ = new BehaviorSubject<SelectableRow<SparePart>[]>([]);
    /**
     * State, that stores the currently checked rows of the main table.
     */
    private checkedRowsState: SelectableRow<SparePart>[] = [];

    public set checkedRows(checkedRows: SelectableRow<SparePart>[]) {
        this.checkedRowsIncludeDetails = checkedRows.some(
            (row) => row.rowData.recommendation?.recommendedParts
        );

        if (this.checkedRowsIncludeDetails === false) {
            this.checkedRowsIncludeDetails = checkedRows.some((row) => row.rowData.descriptionHtml);
        }
        this.checkedRowsState = checkedRows;
        this.checkedRows$.next(checkedRows);
        // set displayed item again, in case all rows with recommendations got unchecked
        this.displayedItem = this.getMarkedRow();
    }

    public get checkedRows() {
        return this.checkedRowsState;
    }

    public readonly getCheckedRows = this.checkedRows$.asObservable();

    /**
     * BehaviorSubject, that holds the currently marked row.
     */
    private displayedItem$ = new BehaviorSubject<SelectableRow<SparePart> | null>(null);

    public readonly getDisplayedItem$ = this.displayedItem$.asObservable();

    public getMarkedRow() {
        return this.displayedItem$.value;
    }

    public set displayedItem(markedRow: SelectableRow<SparePart> | null) {
        this.isMarkedRowWithDetails =
            markedRow !== null && this.checkedRows.includes(markedRow) && this.checkedRowsIncludeDetails;

        // add more conditions, on when the part detail should be shown here:
        if (this.isMarkedRowWithDetails || markedRow?.rowData.recommendation) {
            this.displayedItem$.next(markedRow);
        } else {
            this.displayedItem$.next(null);
        }
    }

    public resetState() {
        this.recommendationChecked$.next(null);
        this.checkedRows = [];
        this.displayedItem = null;
        this.isMarkedRowWithDetails = false;
        this.checkedRowsIncludeDetails = false;
    }

    /**
     * Select and mark the previously checked item.
     */
    public selectPrevious() {
        const markedRow = this.getMarkedRow();

        if (!markedRow || !this.checkedRowsIncludeDetails) {
            return;
        }

        const arrIndex = this.checkedRows.findIndex((checkedRow) => checkedRow === markedRow);

        if (arrIndex > 0) {
            this.displayedItem = this.checkedRows[arrIndex - 1];
        } else {
            this.displayedItem = this.checkedRows[this.checkedRows.length - 1];
        }
    }

    /**
     * Select and mark the next checked item.
     */
    public selectNext() {
        const markedRow = this.getMarkedRow();

        if (!markedRow || !this.checkedRowsIncludeDetails) {
            return;
        }

        const arrIndex = this.checkedRows.findIndex((checkedRow) => checkedRow === markedRow);

        if (arrIndex < this.checkedRows.length - 1) {
            this.displayedItem = this.checkedRows[arrIndex + 1];
        } else {
            this.displayedItem = this.checkedRows[0];
        }
    }
}
